import produce from 'immer';
import { Component, h } from 'preact';
import { Localizer } from 'preact-i18n';
import * as appStyles from '../../../assets/global.scss';
import Icon from '../../../common/Icon/Icon';
import {
  AmDurationType,
  AmHolidayType,
  AmLocation,
  Calendar,
  ClientType,
  getKey,
  HistogramData,
  Key,
  Month,
  PaxAmount,
  Price,
  Prices,
  Properties,
  TravelDates,
  TravelType,
  WidgetTab,
} from '../../../common';
import { Column } from '../../../common/Layout/Layout';
import { FinText, PriceFormat } from '../../../i18n';
import {
  Modal,
  MODAL_TITLE,
  ModalBody,
  ModalButton,
  ModalClose,
  ModalContext,
  ModalFooter,
  ModalHeader,
} from '../../../modal';
import { Actions, actions, AmAvailability, BookingState, connectStore } from '../../../store';
import { withStyles, WithStyles } from '../../../style';
import { isNotPresent, isPresent, stopPropagation } from '../../../utils';
import CalendarMonth from './/CalendarMonth/CalendarMonth';
import * as styles from './DateModal.scss';
import {
  amAvailabilityIsPresent,
  amSelectionStartForDeparture,
  DateInput,
  DateRange,
  fromIso8601,
  getCalendarRange,
  getInputRange,
  selectionStartForDeparture,
  toIso8601,
  totalPriceOptions,
  validateLocalDate,
} from './dateModalUtils';
import { Histogram } from './Histogram/Histogram';

export interface DateModalProps extends Properties {
  isAward?: boolean;
  locale: string;
  clientType?: ClientType;
}

interface Input {
  value: string;
  valid: boolean;
  dirty: boolean;
}

export interface DateModalState {
  selectionStart: number;
  selectedMonth: number;
  departureInput: Input;
  returnInput: Input;
  inputActive: boolean;
  hoverDay: string;
}

interface StoreProps {
  calendar: Calendar;
  travelDates: TravelDates;
  travelType: TravelType;
  paxAmount: PaxAmount;
  fullYear?: Prices;
  followingMonth?: Prices;
  startingFrom?: Price;
  onAmTab: boolean;
  amHolidayType?: AmHolidayType;
  amDestination?: AmLocation;
  amAvailability?: AmAvailability;
  amDuration?: string;
  histogramData: HistogramData;
}

type EffectiveProps = DateModalProps & StoreProps & Actions & WithStyles;

const mapStateToProps = ({
  selection,
  ux,
  price,
  currentTab,
  testVariant,
  amState: { amSelection, availability },
}: BookingState): StoreProps => {
  const defaultMappings = {
    calendar: ux.calendar,
    travelDates: selection.travelDates,
    travelType: selection.travelType,
    paxAmount: selection.paxAmount,
    fullYear: price.fullYear,
    followingMonth: price.followingMonth,
    startingFrom: price.startingFrom,
    onAmTab: currentTab === WidgetTab.AM,
    histogramData: price.histogramData,
    locale: ux.i18n.locale,
    testVariant,
  };
  const amMappings = {
    travelDates: amSelection.amTravelDates,
    travelType: amSelection.travelType,
    fullYear: undefined,
    followingMonth: undefined,
    amHolidayType: amSelection.locations?.holidayType,
    amDestination: amSelection.locations?.destination,
    amAvailability: availability,
    amDuration: amSelection.duration,
  };

  return {
    ...defaultMappings,
    ...(currentTab === WidgetTab.AM && amMappings),
  };
};

@withStyles(styles, appStyles)
@connectStore({ mapStateToProps, actions })
export default class DateModal extends Component<DateModalProps, DateModalState> {
  state = {
    selectionStart: 0,
    selectedMonth: 0,
    departureInput: { value: '', valid: true, dirty: false },
    returnInput: { value: '', valid: true, dirty: false },
    inputActive: false,
    hoverDay: undefined,
  };

  preventScrollHandler = false;

  scrollToSelection = (): void => {
    requestAnimationFrame(() => {
      const selectionElem: HTMLElement = isPresent(this.base)
        ? (this.base as HTMLElement).querySelector('.bw-selection-scroll')
        : undefined;
      if (isPresent(selectionElem?.scrollIntoView)) {
        selectionElem.scrollIntoView({ behavior: 'smooth' });
      }
    });
  };

  constructor() {
    super();
    this.scrollHandler = this.scrollHandler.bind(this);
  }

  componentDidMount(): void {
    this.updateInputValues(this.props as EffectiveProps);
    this.scrollToSelection();

    if (this.props.clientType === ClientType.MOBILE_TABLET) {
      (this.base as HTMLElement).querySelector('.bw-content').addEventListener('scroll', this.scrollHandler);
    }
  }

  componentWillUnmount() {
    if (this.props.clientType === ClientType.MOBILE_TABLET) {
      (this.base as HTMLElement).querySelector('.bw-content').removeEventListener('scroll', this.scrollHandler);
    }
  }

  componentWillReceiveProps(nextProps: EffectiveProps): void {
    this.updateInputValues(nextProps);
  }

  scrollHandler(event: Event): void {
    event.preventDefault();

    const checkIfMonthInViewPort = (element: HTMLElement): boolean => {
      const histogramElement = (this.base as HTMLElement)?.querySelector('.bw-histogram');
      if (isPresent(histogramElement)) {
        const { top, height } = histogramElement.getBoundingClientRect();
        const { top: elementTop, height: elementHeight } = element.getBoundingClientRect();

        // To be sure that visible month will be visible till almost the end of the month height.
        return top + height / 2 < elementTop + elementHeight / 2;
      }

      return false;
    };

    const list = document.getElementsByClassName('bw-monthWrapper');
    let monthId = 0;
    for (let i = 0; i < list.length; i++) {
      const element = list.item(i) as HTMLElement;
      if (checkIfMonthInViewPort(element)) {
        monthId = parseInt(element.getAttribute('data-month-id'), 10);
        break;
      }
    }

    if (!this.preventScrollHandler) {
      this.setState((prevState) =>
        produce(prevState, (draft: DateModalState) => {
          draft.selectedMonth = monthId;
        })
      );
    }
  }

  updateInputValues = (props: EffectiveProps): void => {
    const { calendar, travelDates, onAmTab, amDestination } = props;
    this.setState((prevState) =>
      produce(prevState, (draft: DateModalState) => {
        draft.departureInput = { value: fromIso8601(travelDates.departureDate), valid: true, dirty: false };
        draft.returnInput = { value: fromIso8601(travelDates.returnDate), valid: true, dirty: false };
        draft.selectionStart = onAmTab
          ? amSelectionStartForDeparture(calendar, travelDates.departureDate, amDestination)
          : selectionStartForDeparture(calendar, travelDates.departureDate);

        draft.selectedMonth = draft.selectionStart;
      })
    );
  };

  incrementSelection = (e: Event): void => {
    const {
      calendar: { months },
    } = this.props as EffectiveProps;
    stopPropagation(e);
    this.setState((prevState) =>
      produce(prevState, (draft: DateModalState) => {
        if (draft.selectionStart < months.length - 2) {
          draft.selectionStart += 1;
        }

        if (draft.selectedMonth < months.length - 1) {
          draft.selectedMonth += 1;
        }
      })
    );
  };

  decrementSelection = (e: Event): void => {
    stopPropagation(e);
    this.setState((prevState) =>
      produce(prevState, (draft: DateModalState) => {
        if (draft.selectionStart > 0) {
          draft.selectionStart -= 1;
          draft.selectedMonth -= 1;
        }
      })
    );
  };

  setSelection = (newSelection: number): void => {
    const {
      calendar: { months },
      clientType,
    } = this.props as EffectiveProps;

    if (clientType === ClientType.MOBILE_TABLET) {
      // This needed this to prevent cycle event calls, because we do horizontal scroll after we selected this month in histogram,
      // and we have a handler witch is select month then we are scrolling months
      this.preventScrollHandler = true;
      setTimeout(() => {
        this.preventScrollHandler = false;
      }, 1000);
    }

    this.setState(
      (prevState) =>
        produce(prevState, (draft: DateModalState) => {
          draft.selectionStart = newSelection < months.length - 1 ? newSelection : newSelection - 1;
          draft.selectedMonth = newSelection;
        }),
      () => {
        if (clientType === ClientType.MOBILE_TABLET) {
          const baseElement = this.base as HTMLElement;
          const scrollContent = baseElement.querySelector('.bw-content');
          const itemPosition = this.getCurrentItemPosition(baseElement);

          scrollContent.scrollTo({ top: itemPosition, behavior: 'smooth' });
        }
      }
    );
  };

  validateInput = (e: Event): void => {
    const { travelDates, calendar } = this.props as EffectiveProps;
    const elem: HTMLInputElement = e.target as HTMLInputElement;
    const input: DateInput = elem.getAttribute('data-type') as DateInput;
    const oldValue: string = this.state[input].value;
    let newValue: string = elem.value;

    // Run replace only for 5 first inserted characters (days and months incl. possible commas).
    // Also check if characters have been deleted and skip automatic comma insertion in that case.
    if (newValue.length < 6 && newValue.length > oldValue.length) {
      newValue = newValue.replace(/(\d{2})(?!\.)/g, '$1.');
    }

    this.setState((prevState) =>
      produce(prevState, (draft: DateModalState) => {
        draft[input] = {
          value: newValue,
          valid: validateLocalDate(newValue, getInputRange(calendar, input, travelDates)),
          dirty: false,
        };
      })
    );
  };

  onInputChange = (): void => {
    const {
      calendar,
      setTravelDates,
      travelDates: { flexible },
    } = this.props as EffectiveProps;
    const { departureInput, returnInput } = this.state;

    const calendarRange: DateRange = getCalendarRange(calendar);
    const technicallyValid: boolean =
      validateLocalDate(departureInput.value, calendarRange) && validateLocalDate(returnInput.value, calendarRange);
    if (!technicallyValid) {
      return;
    }

    if (!departureInput.valid || !returnInput.valid) {
      return;
    }

    const departureDate: string = toIso8601(departureInput.value);
    const returnDate: string = toIso8601(returnInput.value);

    setTravelDates({ departureDate, returnDate, flexible });

    this.scrollToSelection();
  };

  onInputFocus = (): void => {
    this.setState((prevState) =>
      produce(prevState, (draft: DateModalState) => {
        draft.inputActive = true;
      })
    );
  };

  onInputBlur = (input: string): void => {
    this.setState((prevState) =>
      produce(prevState, (draft: DateModalState) => {
        draft.inputActive = false;
        draft[input].dirty = true;
      })
    );
  };

  onInputKeyUp = (e: KeyboardEvent): void => {
    if (getKey(e) !== Key.ENTER || isNotPresent(this.base)) {
      return;
    }
    const input: DateInput = (e.target as HTMLInputElement).getAttribute('data-type') as DateInput;
    const selector: string = input === DateInput.DEPARTURE ? `#bw-${DateInput.RETURN}` : '.bw-modal-done';
    const nextElem: HTMLElement = (this.base as HTMLElement).querySelector(selector);
    if (isPresent(nextElem)) {
      nextElem.focus();
    }
  };

  onSelectDate = (date: string): void => {
    const {
      travelDates: { departureDate, returnDate, flexible },
      travelType,
      setTravelDates,
      isAward,
    } = this.props as EffectiveProps;
    if (
      travelType === TravelType.ONE_WAY ||
      isNotPresent(departureDate) ||
      departureDate > date ||
      isPresent(returnDate)
    ) {
      setTravelDates({ departureDate: date, flexible: isAward || flexible, returnDate: undefined });
    } else {
      setTravelDates({ departureDate, flexible: isAward || flexible, returnDate: date });
    }
  };

  onChangeFlexible = (e: Event): void => {
    const {
      travelDates: { departureDate, returnDate },
      travelType,
      setTravelDates,
    } = this.props as EffectiveProps;
    if (travelType === TravelType.ONE_WAY) {
      setTravelDates({ departureDate, flexible: (e.target as HTMLInputElement).checked, returnDate: undefined });
      return;
    }
    setTravelDates({ departureDate, flexible: (e.target as HTMLInputElement).checked, returnDate });
  };

  resetDates = (e: Event): void => {
    stopPropagation(e);
    const {
      setTravelDates,
      travelDates: { flexible },
    } = this.props as EffectiveProps;
    setTravelDates({ flexible });
  };

  isFooterSticky = (): boolean => {
    if (this.state.inputActive) {
      return false;
    }
    const { travelDates, travelType, onAmTab } = this.props as EffectiveProps;
    if (onAmTab) {
      return true;
    }
    const hasDeparture = !!travelDates?.departureDate;
    const hasReturnDate = !!travelDates?.returnDate;
    if (travelType === TravelType.ROUND_TRIP) {
      return hasDeparture && hasReturnDate;
    }
    return hasDeparture;
  };

  onHoverEnd = (day: string): void => {
    this.setState((prevState) => ({ ...prevState, hoverDay: day }));
  };

  shouldShowHistogram = (histogramData: HistogramData, isAward: boolean): boolean => {
    return histogramData?.months.length > 0 && !isAward;
  };

  private getCurrentItemPosition(baseElement: HTMLElement): number {
    const item = baseElement.querySelector('.bw-selection-start') as HTMLElement;

    // Calculate sticky content height
    const stickyContentHeight =
      baseElement.querySelector('.bw-titleArea').getBoundingClientRect().height +
      baseElement.querySelector('.bw-sticky').getBoundingClientRect().height;

    const padding = 18;

    return item.offsetTop - stickyContentHeight + padding;
  }

  render(
    {
      calendar,
      travelDates,
      travelType,
      fullYear,
      followingMonth,
      startingFrom,
      onAmTab,
      amHolidayType,
      amDestination,
      amAvailability,
      amDuration,
      tx,
      isAward,
      locale,
      histogramData,
    }: EffectiveProps,
    { selectionStart, departureInput, returnInput, hoverDay, selectedMonth }: DateModalState,
    { modal }: ModalContext
  ): h.JSX.Element {
    const oneway: boolean = travelType === TravelType.ONE_WAY;
    const shouldDisplayDepartureDateError = !departureInput.valid && departureInput.dirty;
    const shouldDisplayReturnDateError = !returnInput.valid && returnInput.dirty;
    const showAmAvailabilityNotice =
      onAmTab && amHolidayType?.type === AmDurationType.LIST && amAvailabilityIsPresent(amAvailability);
    const showHistogram = this.shouldShowHistogram(histogramData, isAward);

    return (
      <Modal class="bw-date-modal" wide={!showHistogram} largeWide={showHistogram}>
        <ModalHeader class={tx('titleArea', 'border-bottom')} compact={showHistogram}>
          <div class={tx('title', 'flex--self-center nordic-blue-900-text font-heading-5')} id={MODAL_TITLE}>
            {oneway ? <FinText id="dateModal.selectDeparture" /> : <FinText id="dateModal.title" />}
          </div>
          <div class={tx('close', 'flex flex--end')}>
            <ModalClose onMouseDown={modal.hideModal} />
          </div>
          <div class={tx('inputs', 'relative flex border rounded ms-xsmall-x')}>
            <div class={tx('flex flex-1 ps-xxsmall-x ps-xsmall-y border-right', { invalid: !departureInput.valid })}>
              <Icon iconName="departure" class={tx('is-small', 'flex--nogrow')} />
              <label for={`bw-${DateInput.DEPARTURE}`} class="is-vishidden">
                <FinText id="dateModal.selectDeparture" />
              </label>
              <Localizer>
                <input
                  aria-describedby="bw-departure-error"
                  aria-invalid={!departureInput.valid}
                  aria-label={<FinText id="dateModal.selectDeparture" />}
                  autocomplete="off"
                  class={tx(
                    'focusInset',
                    'flex-1 font-body-1 regular-type nordic-blue-900-text border-none mr-xxsmall-l'
                  )}
                  data-type={DateInput.DEPARTURE}
                  id={`bw-${DateInput.DEPARTURE}`}
                  maxLength={10}
                  onChange={this.onInputChange}
                  onFocus={this.onInputFocus}
                  onBlur={() => {
                    this.onInputBlur('departureInput');
                  }}
                  onKeyUp={this.onInputKeyUp}
                  onInput={this.validateInput}
                  placeholder={(<FinText id="dateModal.inputPlaceholder" />) as any as string}
                  title={(<FinText id="dateModal.dateFormatDescription" />) as any as string}
                  type="text"
                  value={departureInput.value}
                  inputMode="numeric"
                />
              </Localizer>
            </div>
            {!oneway && (
              <div class={tx('flex flex-1 ps-xsmall-l ps-xxsmall-r ps-xsmall-y', { invalid: !returnInput.valid })}>
                <Icon iconName="arrival" class={tx('is-small', 'flex--nogrow')} />
                <label for={`bw-${DateInput.RETURN}`} class="is-vishidden">
                  <FinText id="dateModal.selectReturn" />
                </label>
                <Localizer>
                  <input
                    aria-describedby="bw-return-error"
                    aria-invalid={!returnInput.valid}
                    aria-label={<FinText id="dateModal.selectReturn" />}
                    autocomplete="off"
                    class={tx(
                      'focusInset',
                      'flex-1 font-body-1 regular-type nordic-blue-900-text border-none mr-xxsmall-l'
                    )}
                    data-type={DateInput.RETURN}
                    id={`bw-${DateInput.RETURN}`}
                    maxLength={10}
                    onChange={this.onInputChange}
                    onFocus={this.onInputFocus}
                    onBlur={() => {
                      this.onInputBlur('returnInput');
                    }}
                    onKeyUp={this.onInputKeyUp}
                    onInput={this.validateInput}
                    placeholder={(<FinText id="dateModal.inputPlaceholder" />) as any as string}
                    title={(<FinText id="dateModal.dateFormatDescription" />) as any as string}
                    type="text"
                    value={returnInput.value}
                    inputMode="numeric"
                  />
                </Localizer>

                {shouldDisplayDepartureDateError && (
                  <div
                    class={tx(
                      'inputError visible',
                      'absolute ps-xxsmall-l font-body-1 error-900-text',
                      'border error-900-border rounded-bottom-left rounded-bottom-right'
                    )}
                    id="bw-departure-error"
                  >
                    <FinText id="dateModal.invalidDate" />
                  </div>
                )}

                {shouldDisplayReturnDateError && (
                  <div
                    class={tx(
                      'inputError visible',
                      'absolute ps-xxsmall-l font-body-1 error-900-text',
                      'border error-900-border rounded-bottom-left rounded-bottom-right'
                    )}
                    id="bw-return-error"
                  >
                    <FinText id="dateModal.invalidDate" />
                  </div>
                )}
              </div>
            )}
          </div>
          <button
            class={tx('reset', 'focusInset', 'font-body-1 regular-type nordic-blue-900-text underline')}
            onClick={this.resetDates}
          >
            <FinText id="dateModal.reset" />
          </button>
        </ModalHeader>

        {showHistogram && (
          <Histogram selectMonth={this.setSelection} selectedMonth={selectedMonth} histogramData={histogramData} />
        )}

        <ModalBody
          class={tx('monthsArea', {
            'histogram-available': showHistogram,
          })}
          aria-hidden
        >
          <button
            class={tx('bw-prev-month controlButton', 'nordic-blue-900-text border nordic-blue-900-border ms-small-t')}
            onClick={this.decrementSelection}
            tabIndex={-1}
          >
            <Icon iconName="chevron-left" class={tx('is-small')} />
          </button>
          <button
            class={tx('bw-next-month controlButton', 'nordic-blue-900-text border nordic-blue-900-border ms-small-t')}
            onClick={this.incrementSelection}
            tabIndex={-1}
          >
            <Icon iconName="chevron-right" class={tx('is-small')} />
          </button>

          <div class={tx('months', `selectionStartsFrom${selectionStart}`, 'flex nowrap')}>
            {calendar.months.map((month: Month, i: number) => [
              i === selectionStart && <div class={tx('bw-selection-scroll')} />,
              <CalendarMonth
                class={tx('month', { 'bw-selection-start': i === selectionStart })}
                hoverDay={hoverDay}
                key={month.id}
                month={month}
                weekdays={calendar.weekdayKeys}
                travelDates={travelDates}
                travelType={travelType}
                fullYear={fullYear}
                followingMonth={followingMonth}
                onSelectDate={this.onSelectDate}
                showPrices={!isAward}
                onAmTab={onAmTab}
                amHolidayType={amHolidayType}
                amDestination={amDestination}
                amAvailability={amAvailability}
                amDuration={amDuration}
                onHoverEnd={this.onHoverEnd}
                locale={locale}
                listId={i}
              />,
            ])}
          </div>
        </ModalBody>
        <ModalFooter class={tx('ps-large-x')} sticky={this.isFooterSticky()} compact={showHistogram}>
          <Column class={tx('paxAmount', 'font-body-1')}>
            <div class={tx('paxAmount-modal-footer', 'flex flex--row flex--end')}>
              {!onAmTab && !isAward && (
                <div class={tx('flex-1 pr-xxsmall-r')}>
                  <div>
                    <span class="ps-xxsmall-r nordic-blue-900-text">
                      <FinText id="startingPrice.title" />
                    </span>
                    <span class="nordic-blue-900-text medium-type nowrap">
                      <PriceFormat price={startingFrom} options={totalPriceOptions} />
                    </span>
                  </div>
                </div>
              )}
              {showAmAvailabilityNotice && (
                <div class={tx('dateModalCheckbox', 'flex flex--middle fill')}>
                  <div class={tx('flex flex--middle flexible-label')}>
                    <span class={tx('ms-xsmall-r')}>
                      <Icon iconName="am-logo" class={tx('is-xsmall')} />
                    </span>
                    <span>
                      <FinText id="aurinkomatkat.calendar.recommendedDates" />
                    </span>
                  </div>
                </div>
              )}
              {!isAward && (
                <div class={tx('checkboxes', 'flex')}>
                  <div
                    class={tx('dateModalCheckbox', 'flex flex--middle fill', {
                      'is-vishidden': onAmTab,
                    })}
                  >
                    <label
                      class={tx('flex flex--middle flexible-label', {
                        'disabled-area': onAmTab,
                      })}
                    >
                      <input
                        id="bw-flexibleDates"
                        type="checkbox"
                        class={tx('is-vishidden')}
                        checked={onAmTab || travelDates.flexible}
                        name="flexible"
                        disabled={onAmTab}
                        onChange={this.onChangeFlexible}
                      />
                      <span class={tx('ms-xsmall-r')}>
                        <Icon
                          iconName={onAmTab || travelDates.flexible ? 'checkbox-selected' : 'checkbox-unselected'}
                          class={tx('is-xsmall', {
                            'nordic-blue-900-text': !onAmTab,
                          })}
                        />
                      </span>
                      <span>
                        <FinText id="dateModal.flexibleDates" />
                      </span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          </Column>
          <ModalButton onClick={modal.hideModal} cta>
            <FinText id="action.done" />
          </ModalButton>
        </ModalFooter>
      </Modal>
    );
  }
}
